'use client';

import {
	Adapter,
	WalletAdapterNetwork,
	WalletReadyState,
} from '@solana/wallet-adapter-base';
import {
	ConnectionProvider,
	WalletProvider,
} from '@solana/wallet-adapter-react';
import axios from 'axios';
import { ThemeProvider } from 'next-themes';
import {
	PropsWithChildren,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import DriftBlockchainEventSubscriberProvider from 'src/providers/driftEvents/driftBlockchainEventSubscriberProvider';
import OrderRecordMatcherProvider from 'src/providers/orderRecordMatcherProvider';
import PHProvider from 'src/providers/posthog/postHogProvider';
import { SWRConfig } from 'swr';
import { DRIFT_WALLET_PROVIDERS } from '../constants/wallets';
import Env, { DriftTheme } from '../environmentVariables/EnvironmentVariables';
import useInterval from '../hooks/useInterval';
import CandlesProvider from '../providers/candles/candleClientProvider';
import OrderSubscriberProvider from '../providers/orderSubscriberProvider';
import SettingsProvider from '../providers/settingsProvider';
import DevTopWrapper from './Dev/DevTopWrapper';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import SafePushProvider from '../providers/safePushProvider';
// import ElfaWidget from './ElfaWidget/ElfaWidget';
import { DriftBlockchainEventSubjectProvider } from '../providers/driftEvents/driftEventSubjectProvider';
import React from 'react';
import { MarketOrderToastSyncProvider } from './MarketOrderToasts/MarketOrderToastSyncProvider';
import { MarketOrderToastEventEmitterProvider } from './MarketOrderToasts/MarketOrderToastEventEmitterProvider';
import OptimisedSubscriptionsProvider from 'src/providers/optimisedDataSubscriptions/optimisedSubscriptionsProvider';
import { PricingSubscriptionProvider } from 'src/providers/pricing/PricingSubscriptionProvider';
import { isWindowDefined } from 'src/utils/browser';
import AsyncAppSetup from './AsyncAppSetup';
import { ThemeSetter } from './ThemeSetter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SelectedMarketInfoProvider } from '../hooks/useInfoForCurrentlySelectedMarket';
import { MarketInfoDisplayDataProvider } from '../hooks/useMarketInfoDisplayData';
import { Config, SwiftClient } from '@velocity-exchange/common';
import { ConfigsManager } from 'src/@types/configs';
import { PredictionMarketConfigs } from 'src/hooks/predictionMarkets/predictionMarketConfigs';
import { SerializedRemoteConfigs, UIMarket } from '@velocity-exchange/common';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import IOSSafariHoverFix from './IOSSafariHoverFix';
import ProfilerWrapper from './ProfilerWrapper';
import { PhantomProvider } from '@phantom/react-sdk';
import { createSolanaPlugin } from '@phantom/browser-sdk/solana';
import {
	SolanaMobileWalletAdapter,
	createDefaultAddressSelector,
	createDefaultAuthorizationResultCache,
} from '@solana-mobile/wallet-adapter-mobile';
import {
	createDefaultAuthorizationCache,
	createDefaultChainSelector,
	createDefaultWalletNotFoundHandler,
	registerMwa,
} from '@solana-mobile/wallet-standard-mobile';
import AppModals from 'src/components/Modals/groups/AppModals';
import AppDrawers from 'src/components/AppDrawers';
import ToastList from 'src/components/Toasts/ToastList';
import PopupOverlay from 'src/components/Popups/PopupOverlay/PopupOverlay';
import PnlModalAssetPreloader from 'src/components/Modals/PnlModal/PnlModalAssetPreloader';
import PrivySetupProvider from 'src/providers/wallets/PrivySetupProvider';
import PrivyWalletProvider from 'src/providers/wallets/PrivyWalletProvider';
import AppInitialLoadProvider from 'src/providers/app/AppInitialLoadProvider/AppInitialLoadProvider';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { SyncWalletBalances } from 'src/hooks/useSyncWalletBalances';
import AccountTradingDataProvider from 'src/hooks/globalSyncs/accountSync/AccountTradingDataProvider';
import { WALLET_NAME_LOCALSTORAGE_KEY } from 'src/hooks/useHandleSelectWalletProvider';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

SwiftClient.init(Env.swiftServerUrl, 'drift-ui');

const queryClient = new QueryClient();

// Solana Wallet Adapter x Privy integration

const MWA_APP_IDENTITY = {
	name: 'Drift',
	uri: 'https://app.drift.trade',
	icon: '/android-chrome-192x192.png',
};

registerMwa({
	appIdentity: MWA_APP_IDENTITY,
	authorizationCache: createDefaultAuthorizationCache(),
	chains: ['solana:devnet', 'solana:mainnet'],
	chainSelector: createDefaultChainSelector(),
	onWalletNotFound: createDefaultWalletNotFoundHandler(),
});

const ProviderComposer_ = ({
	contexts,
	children,
}: {
	contexts: React.ReactNode[];
	children: React.ReactNode;
}) =>
	contexts.reduceRight(
		(kids, parent) =>
			React.cloneElement(
				parent as React.ReactElement<
					React.ReactNode,
					string | React.JSXElementConstructor<any>
				>,
				{
					children: kids,
				}
			),
		children
	);

const ProviderComposer = React.memo(ProviderComposer_);

const useHandleMarketRemoteMarketConfigs = (
	asyncConfigs: SerializedRemoteConfigs,
	errorFetchingConfig: boolean
) => {
	const [isConfigsLoaded, setIsConfigsLoaded] = useState(false);

	const deserializedConfigs = useMemo(
		() => ConfigsManager.deserializeRemoteConfigs(asyncConfigs),
		[
			asyncConfigs?.marketConfigs.devnet.spot.length,
			asyncConfigs?.marketConfigs.devnet.perp.length,
			asyncConfigs?.marketConfigs.mainnet.spot.length,
			asyncConfigs?.marketConfigs.mainnet.perp.length,
			asyncConfigs?.predictionMarkets.length,
		]
	);

	const memoPerpMarkets = useMemo(() => {
		return deserializedConfigs.perpMarketsConfig;
	}, [deserializedConfigs.perpMarketsConfig.length]);
	const memoSpotMarkets = useMemo(() => {
		return deserializedConfigs.spotMarketsConfig;
	}, [deserializedConfigs.spotMarketsConfig.length]);
	const memoPredictionMarkets = useMemo(() => {
		return deserializedConfigs.predictionMarketsConfig;
	}, [deserializedConfigs.predictionMarketsConfig.length]);

	// Set all remote configs
	// Don't place these in useEffect, since useEffect will only run after the first render
	// useLayoutEffect also cause some issues - the provider composer component somehow doesn't pick up the remote configs before the first render
	PredictionMarketConfigs.setConfigs(memoPredictionMarkets);
	UIMarket.setPerpMarkets(memoPerpMarkets);
	UIMarket.setSpotMarkets(memoSpotMarkets);
	Config.perpMarketsLookup = memoPerpMarkets;
	Config.spotMarketsLookup = memoSpotMarkets;

	const updateSelectedMarketConfigWithNewRemoteConfigs = () => {
		useDriftStore.getState().set((s) => {
			const selectedMarket = useDriftStore.getState().selectedMarket.current;

			if (!selectedMarket) {
				return;
			}

			if (selectedMarket.isPerp) {
				s.selectedMarket.current = UIMarket.createPerpMarket(
					selectedMarket.marketIndex
				);
			} else {
				s.selectedMarket.current = UIMarket.createSpotMarket(
					selectedMarket.marketIndex
				);
			}
		});
	};

	useLayoutEffect(() => {
		if (
			memoPredictionMarkets.length > 0 &&
			memoSpotMarkets.length > 0 &&
			memoPerpMarkets.length > 0
		) {
			setIsConfigsLoaded(true);
			updateSelectedMarketConfigWithNewRemoteConfigs();
		}
	}, [memoPerpMarkets, memoSpotMarkets, memoPredictionMarkets]);

	useEffect(() => {
		errorFetchingConfig &&
			console.error('Error fetching configs', errorFetchingConfig);
	}, [errorFetchingConfig]);

	return isConfigsLoaded;
};

type AppWrapperProps = PropsWithChildren<{
	asyncConfigs: SerializedRemoteConfigs;
	errorFetchingConfig: boolean;
}>;

/**
 * Note: previously this component was in charge of running async app setup hooks once the window.load event fired. After updating to React 18 the window.load event stopped firing - so that functionality has temporarily been disabled. That is why we still have a "liteStore" and "onLoad" event hook which aren't doing anything. Once SSR has been properly set up on the app which is the goal of updating to react 18 anyway, then the problem this component was trying to solve will be finished.
 * @param props
 * @returns
 */
const AppWrapper = (props: AppWrapperProps) => {
	const isConfigsLoaded = useHandleMarketRemoteMarketConfigs(
		props.asyncConfigs,
		props.errorFetchingConfig
	);

	const [e2eWallet, setE2eWallet] = useState<Adapter | undefined>(undefined);

	useInterval(() => {
		const E2E_WALLET = Env.E2E_WALLET;

		if (isWindowDefined() && window.useE2eWallet === true) {
			setE2eWallet(E2E_WALLET);
		} else {
			if (E2E_WALLET?.connected) {
				E2E_WALLET.disconnect();
			}
			setE2eWallet(undefined);
		}
	}, 1000);

	const wallets = useMemo(
		() => {
			const extraWallets = [
				e2eWallet ?? undefined,
				// we add the Solana MWA here too, so that users see it as a wallet option when connecting to Drift
				new SolanaMobileWalletAdapter({
					chain:
						Env.sdkConfig.ENV === 'mainnet-beta'
							? WalletAdapterNetwork.Mainnet
							: WalletAdapterNetwork.Devnet,
					addressSelector: createDefaultAddressSelector(),
					appIdentity: MWA_APP_IDENTITY,
					authorizationResultCache: createDefaultAuthorizationResultCache(),
					onWalletNotFound: async () => {}, // do nothing; default handler (createDefaultWalletNotFoundHandler) doesn't gel well with our UI
				}) as unknown as Adapter,
			].filter((wallet) => !!wallet);

			return [...DRIFT_WALLET_PROVIDERS, ...extraWallets];
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[e2eWallet]
	);

	const sortedWallets = useMemo(
		() =>
			[...wallets].sort((a, b) => {
				const aIsInstalled =
					a?.readyState === WalletReadyState.Loadable ||
					a?.readyState === WalletReadyState.Installed;

				const bIsInstalled =
					b?.readyState === WalletReadyState.Loadable ||
					b?.readyState === WalletReadyState.Installed;

				if (aIsInstalled && !bIsInstalled) return -1;
				if (aIsInstalled === bIsInstalled) return 0;
				return 1;
			}),
		[wallets]
	);

	const memoizedProviders = useMemo(() => {
		return [
			<QueryClientProvider key="QueryClientProvider" client={queryClient} />,
			// @ts-ignore
			<ConnectionProvider
				key="ConnectionProvider"
				endpoint={Env.defaultRpcAddress}
			/>,
			// @ts-ignore
			<WalletProvider
				key="WalletProvider"
				wallets={sortedWallets}
				// We run our own custom autoconnect logic in the useKeepWalletsInSync hook
				autoConnect={true}
				localStorageKey={WALLET_NAME_LOCALSTORAGE_KEY}
			/>,
			// @ts-ignore
			<PhantomProvider
				config={{ plugins: [createSolanaPlugin()] }}
				key="PhantomProvider"
			/>,
			// @ts-ignore
			<PrivySetupProvider key="PrivyProvider" />,
			<PrivyWalletProvider key="PrivyWalletProvider" />,
			<SettingsProvider key="SettingsProvider" />,
			<SelectedMarketInfoProvider key="SelectedMarketInfoProvider" />,
			<MarketInfoDisplayDataProvider key="MarketInfoDisplayDataProvider" />,
			<OrderSubscriberProvider key="OrderSubscriberProvider" />,
			<DriftBlockchainEventSubscriberProvider key="DriftBlockchainEventSubscriberProvider" />,
			<DriftBlockchainEventSubjectProvider key="DriftBlockchainEventSubjectProvider" />,
			<CandlesProvider key="CandlesProvider" />,
			<OrderRecordMatcherProvider key="OrderRecordMatcherProvider" />,
			<MarketOrderToastEventEmitterProvider key="MarketOrderToastEventProvider" />,
			<MarketOrderToastSyncProvider key="MarketOrderToastSyncProvider" />,
			<OptimisedSubscriptionsProvider key="OptimisedSubscriptionsProvider" />,
			<PricingSubscriptionProvider key="PricingSubscriptionProvider" />,
			// @ts-ignore
			<NuqsAdapter key="NuqsAdapter" />,
			<AccountTradingDataProvider key="AccountTradingDataProvider" />,
		].filter((provider) => provider);
	}, [Env.defaultRpcAddress, sortedWallets]);

	return (
		<>
			<ProfilerWrapper>
				<ThemeProvider
					attribute="class"
					themes={[DriftTheme.dark, DriftTheme.light]}
					enableSystem={false}
					defaultTheme={DriftTheme.dark}
					disableTransitionOnChange
				>
					<SafePushProvider>
						<PHProvider>
							<SWRConfig
								value={{
									fetcher: (url: string) =>
										axios.get(url).then((res) => res.data),
								}}
							>
								<ProviderComposer contexts={memoizedProviders}>
									<AppInitialLoadProvider>
										{/* Need this component to make the theme available to logic outside of the Theme Provider */}
										<ThemeSetter />
										<IOSSafariHoverFix />
										<DevTopWrapper />
										{/** Ideally isConfigsLoaded should wrap <ProviderComposer />, but that caused some issues */}
										{isConfigsLoaded && props.children}
										<AppModals />
										<AppDrawers />
										<ToastList />
										<PopupOverlay />
										<PnlModalAssetPreloader />
										<AsyncAppSetup />
										{/* <ElfaWidget /> */}
										{isConfigsLoaded && <SyncWalletBalances />}
										{/** Commented out because we don't use RQ for that much. Uncomment whenever you need to debug. */}
										{/* <ReactQueryDevtools initialIsOpen={false} /> */}
									</AppInitialLoadProvider>
								</ProviderComposer>
							</SWRConfig>
						</PHProvider>
					</SafePushProvider>
				</ThemeProvider>
			</ProfilerWrapper>
		</>
	);
};

export default AppWrapper;
